import React, { useEffect, useState } from 'react';
import { FiClock } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { AuthButton } from '../common/AuthButton';
import PasswordInput from './PasswordInput';
import TextInput from './TextInput';
import { useAuthStore } from '../../context/useAuthStore';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../constant/routes';

const OTP_LENGTH = 6;
const INITIAL_TIMER = 2 * 60;

interface ResetPasswordFormProps {
  onSuccess?: () => void;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onSuccess }) => {
  const { resetPassword, resendOtp, forgotPasswordEmail } = useAuthStore();
  const [form, setForm] = useState({ otp: '', password: '' });
  const [errors, setErrors] = useState<{ otp: string; password: string }>({ otp: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIMER);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timerId = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timerId);
  }, [timeLeft]);

  const formatTime = (sec: number) =>
    `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'otp' && !/^\d{0,6}$/.test(value)) return;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { otp: '', password: '' };
    if (form.otp.length !== OTP_LENGTH) {
      newErrors.otp = `OTP must be exactly ${OTP_LENGTH} digits`;
      isValid = false;
    }
    if (!form.password.trim()) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    if (!forgotPasswordEmail) {
      toast.error('Email not found. Please initiate forgot password again.');
      return;
    }
    setLoading(true);
    try {
      const success = await resetPassword(forgotPasswordEmail, form.otp, form.password);
      if (success) {
        setForm({ otp: '', password: '' });
        setTimeout(() => onSuccess?.(), 700);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error?.message || 'Reset failed. Please try again.');
      } else {
        toast.error('Reset failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!forgotPasswordEmail) {
      toast.error('Email not found. Please initiate forgot password again.');
      return;
    }
    try {
      setTimeLeft(INITIAL_TIMER);
      setForm(prev => ({ ...prev, otp: '' }));
      await resendOtp();
      toast.success('OTP resent!');
    } catch {
      toast.error('Failed to resend OTP. Please try again later.');
    }
  };

  const isFormValid =
    form.otp.length === OTP_LENGTH &&
    form.password.length >= 8 &&
    !errors.otp &&
    !errors.password;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <TextInput
        id="otp"
        name="otp"
        label="OTP"
        type="text"
        value={form.otp}
        onChange={handleChange}
        placeholder="Enter 6-digit OTP"
        inputMode="numeric"
        maxLength={OTP_LENGTH}
        required
        error={!!errors.otp}
        errorMessage={errors.otp}
        newClassName="text-center"
      />
      <div className="rememberMeContainer flex items-center justify-between">
        {timeLeft > 0 ? (
          <div className="timer flex items-center gap-1 text-gray-600 text-sm" aria-live="polite">
            <FiClock aria-hidden="true" /> {formatTime(timeLeft)}
          </div>
        ) : (
          <button
            type="button"
            className="forgotPasswordLink"
            onClick={handleResendOtp}
            aria-label="Resend OTP"
          >
            Resend OTP
          </button>
        )}
        <Link
          to={ROUTES.FORGOT_PASSWORD}
          className="forgotPasswordLink"
          aria-label="Change email"
        >
          Change email?
        </Link>
      </div>
      <PasswordInput
        id="password"
        name="password"
        label="New Password"
        value={form.password}
        onChange={handleChange}
        placeholder="Enter your new password"
        required
        errorMessage={errors.password}
        disabled={loading}
      />
      <AuthButton
        title="Reset Password"
        loadingTitle="Resetting..."
        loading={loading}
        disabled={!isFormValid || loading}
      />
    </form>
  );
};
